/**
 * Error response types matching backend error models.
 *
 * Backend (apps/dashboard-backend/middleware/exception_handler.py) emits an
 * RFC 7807 ``application/problem+json`` envelope. STD-020 PR B stripped the
 * pre-STD-020 legacy fields (``error / message / timestamp / field_errors``)
 * from the body once the frontend's prefer-RFC-7807 reads landed (#945).
 * Remaining non-RFC fields (``error_code``, ``details``, ``operation``,
 * ``table``, ``bucket``, ``key``) are RFC 7807 §3.2 extension members kept
 * for support triage and frontend log breadcrumbs.
 */

import { AxiosError } from 'axios';

/**
 * RFC 7807 problem object (https://datatracker.ietf.org/doc/html/rfc7807).
 *
 * - ``type`` is a URI reference identifying the error class. Defaults to
 *   ``about:blank`` per the RFC when no per-class URI is configured.
 * - ``title`` is a short, stable summary of the error class.
 * - ``status`` is the HTTP status code in the body.
 * - ``detail`` is the human-readable explanation specific to this
 *   occurrence — what the frontend renders to the user.
 * - ``instance`` is the request path of this specific occurrence.
 * - ``request_id`` is a documented extension member for cross-service
 *   correlation; always present.
 * - ``validation_errors`` is a documented extension member carrying the
 *   field-level Pydantic failure array; only present on 422 handlers that
 *   carry field-level data.
 */
export interface Problem {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance: string;
  request_id?: string | null;
  validation_errors?: ValidationError[] | null;
}

/**
 * One entry in ``Problem.validation_errors`` — matches FastAPI / Pydantic's
 * ``RequestValidationError.errors()`` shape after the ``ctx`` / ``input``
 * fields are stripped server-side (CWE-209). The custom-``ValidationError``
 * handler translates its legacy ``{field: msg}`` dict into the same shape
 * (loc: [field], msg, type: "value_error") so the frontend has a single
 * render path.
 */
export interface ValidationError {
  loc: Array<string | number>;
  msg: string;
  type: string;
}

/**
 * Standard error response envelope.
 *
 * Extends ``Problem`` (RFC 7807 fields) and adds the seam's surviving
 * extension members: ``error_code`` (used by ``extractErrorCode`` for log
 * triage) and ``details`` (sanitized debug context, read by
 * ``extractErrorDetails`` for Sentry breadcrumbs).
 */
export interface ErrorResponse extends Problem {
  error_code?: string | null;
  details?: Record<string, unknown> | null;
}

/**
 * Validation-error envelope. After PR B the field-level data lives in
 * ``Problem.validation_errors`` (inherited); this alias kept so existing
 * call sites that named the shape don't need to be retyped.
 */
export type ValidationErrorResponse = ErrorResponse;

/**
 * Database error — keeps ``operation`` / ``table`` extension members.
 */
export interface DatabaseErrorResponse extends ErrorResponse {
  operation?: string | null;
  table?: string | null;
}

/**
 * S3 error — keeps ``operation`` / ``bucket`` / ``key`` extension members.
 */
export interface S3ErrorResponse extends ErrorResponse {
  operation?: string | null;
  bucket?: string | null;
  key?: string | null;
}

/**
 * Union type for all possible error responses from the API.
 */
export type ApiErrorResponse =
  | ErrorResponse
  | ValidationErrorResponse
  | DatabaseErrorResponse
  | S3ErrorResponse;

/**
 * Typed Axios error with our error response structure.
 */
export type AxiosErrorWithResponse = AxiosError<ApiErrorResponse>;

/**
 * Type guard to check if an error is an Axios error with response.
 */
export function isAxiosErrorWithResponse(error: unknown): error is AxiosErrorWithResponse {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as { isAxiosError?: boolean }).isAxiosError === true &&
    'response' in error &&
    typeof (error as { response?: unknown }).response === 'object' &&
    (error as { response?: unknown }).response !== null
  );
}

/**
 * Type guard for validation error responses (post PR B: single source of
 * truth is ``Problem.validation_errors``).
 */
export function isValidationErrorResponse(
  response: ApiErrorResponse
): response is ValidationErrorResponse {
  return (
    'validation_errors' in response &&
    response.validation_errors !== undefined &&
    response.validation_errors !== null
  );
}

/**
 * Type guard to check if an error response is a database error.
 */
export function isDatabaseErrorResponse(
  response: ApiErrorResponse
): response is DatabaseErrorResponse {
  return 'operation' in response && 'table' in response;
}

/**
 * Type guard to check if an error response is an S3 error.
 */
export function isS3ErrorResponse(response: ApiErrorResponse): response is S3ErrorResponse {
  return 'operation' in response && 'bucket' in response;
}
